import React, { useEffect, useState } from 'react';
import {
  Sheet, Button, Field, TextArea, TextInput, ChoiceChips, WizardStep,
  ResultCard, ExpertDisclosure, color,
} from '../../design';
import type { Choice, ResultLine } from '../../design';
import {
  marketplaceApi,
  type MarketplaceManifest,
  type OrchestratedManifestResult,
  type OrchestratorQuestion,
} from '../../api/marketplaceApi';
import { haptic } from '../../telegram';

/*
  Phase B — Reusable «скажи словами → готовый результат» creator.

  Wraps the EXISTING backend handles unchanged:
    • orchestrate-clarify  → ≤ questions with options (one per wizard step)
    • orchestrate-manifest → a human plan + requirements (может переспросить)
    • install-manifest     → creates the automation/agent
  No technical fields on the surface; cron/webhook/tier stay hidden — кроме
  времени запуска и названия, которые можно поправить прямо на экране подтверждения.
*/

export type SmartCreateKind = 'pipeline' | 'agent';

interface SmartCreateProps {
  open: boolean;
  initialPrompt?: string;
  onClose: () => void;
  onInstalled?: (result: { kind: string; title?: string; lines?: ResultLine[]; tools?: string[] }) => void;
}

type Step = 'describe' | 'clarify' | 'building' | 'review';

type RequirementView = {
  ref: string;
  label: string;
  type: string;
  required: boolean;
  description?: string | null;
  default?: string | number | null;
};

interface PreviewData {
  title: string;
  emoji: string;
  description: string;
  lines: ResultLine[];
  tools: string[];
}

const KIND_OPTIONS: Choice<SmartCreateKind>[] = [
  { key: 'pipeline', label: 'Автоматизация' },
  { key: 'agent', label: 'Только агент' },
];

// «Свой вариант»: раньше LLM иногда сама добавляла такой вариант строкой,
// и нажатие отправляло в план буквально фразу «Свой вариант» — без содержания.
// Теперь такие варианты вырезаем и подставляем свой — с полем ввода.
const CUSTOM_KEY = '__custom__';
const CUSTOM_LABEL = 'Свой вариант';

function isCustomOption(s: string): boolean {
  return /^(сво[йёея]|друг[ойое]|иное|ввести|указать|other|custom)\b/i.test(s.trim());
}

function humanSchedule(cron?: string): string {
  const m = /^(\d{1,2})\s+(\d{1,2})\s+\*\s+\*\s+\*$/.exec(String(cron || '').trim());
  if (m) {
    const hh = String(Number(m[2])).padStart(2, '0');
    const mm = String(Number(m[1])).padStart(2, '0');
    return `Каждый день в ${hh}:${mm}`;
  }
  return 'По расписанию';
}

/** Время из cron в виде ЧЧ:ММ (для редактируемого поля). */
function cronTime(cron?: string): string | null {
  const m = /^(\d{1,2})\s+(\d{1,2})\s+/.exec(String(cron || '').trim());
  if (!m) return null;
  return `${String(Number(m[2])).padStart(2, '0')}:${String(Number(m[1])).padStart(2, '0')}`;
}

/** Подставляет новое время в cron, не трогая дни недели/месяца. */
function withCronTime(cron: string, hhmm: string): string {
  const t = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!t) return cron;
  const parts = String(cron || '').trim().split(/\s+/);
  if (parts.length < 5) return cron;
  parts[0] = String(Number(t[2]));
  parts[1] = String(Number(t[1]));
  return parts.join(' ');
}

function previewFromManifest(manifest: MarketplaceManifest): PreviewData {
  const agent = (manifest.agents?.[0] ?? {}) as Record<string, unknown>;
  const tools = Array.isArray(agent.enabled_builtins)
    ? (agent.enabled_builtins as unknown[]).map(String)
    : [];
  const title = String(agent.name || (manifest.kind === 'agent' ? 'Новый агент' : 'Новая автоматизация'));
  const description = String(agent.description || 'Готово к запуску.');
  const trigger = manifest.trigger as Record<string, unknown> | undefined;
  let when = 'Запуск вручную или из чата';
  if (trigger?.type === 'schedule') {
    when = humanSchedule(trigger.cron as string | undefined);
  } else if (trigger?.type === 'inbound_webhook') {
    when = 'При входящем событии';
  }
  const response = manifest.response as Record<string, unknown> | undefined;
  const target = (response?.target as string | undefined) ?? (manifest.kind === 'agent' ? 'chat' : 'telegram');
  const sendsTo =
    target === 'telegram' ? 'Пришлёт в Telegram'
    : target === 'both' ? 'В Telegram и во внешний сервис'
    : target === 'outbound_webhook' ? 'Во внешний сервис'
    : target === 'none' ? 'Сохранит без отправки'
    : 'Ответит в чат';
  const lines: ResultLine[] = [
    { label: 'Что', value: description },
    { label: 'Когда', value: when },
    { label: 'Куда', value: sendsTo },
  ];
  return { title, emoji: manifest.kind === 'agent' ? '🤖' : '⚙️', description, lines, tools };
}

function composePrompt(base: string, questions: OrchestratorQuestion[], answers: Record<string, string>): string {
  const lines = [base.trim()];
  for (const q of questions) {
    const v = String(answers[q.id] ?? '').trim();
    if (v) lines.push(`${q.text} — ${v}`);
  }
  return lines.join('\n');
}

/** Сводит выбранные чипы и свободный текст в обычные ответы. */
function resolveAnswers(
  questions: OrchestratorQuestion[],
  answers: Record<string, string>,
  custom: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const q of questions) {
    const v = answers[q.id];
    if (!v) continue;
    const text = v === CUSTOM_KEY ? String(custom[q.id] ?? '').trim() : v;
    if (text) out[q.id] = text;
  }
  return out;
}

function fieldType(type: string): string {
  if (type === 'secret' || type === 'byok') return 'password';
  if (type === 'number') return 'number';
  if (type === 'url') return 'url';
  return 'text';
}

const introText: React.CSSProperties = { fontSize: 14, color: color.textMuted, marginBottom: 12 };
const errorStyle: React.CSSProperties = { background: color.dangerSoft, color: color.danger, padding: '10px 12px', borderRadius: 12, fontSize: 14, marginTop: 12 };
const noteStyle: React.CSSProperties = { background: color.surfaceRaised, border: `1px solid ${color.border}`, borderRadius: 12, padding: '10px 12px', fontSize: 14, color: color.text, marginBottom: 12 };
const noteItem: React.CSSProperties = { fontSize: 13, color: color.textMuted, marginTop: 4 };
const loadingStyle: React.CSSProperties = { padding: '36px 0', textAlign: 'center', color: color.textMuted, fontSize: 15 };
const footerWrap: React.CSSProperties = { marginTop: 16 };
const sectionWrap: React.CSSProperties = { marginTop: 16 };
const sectionTitle: React.CSSProperties = { fontSize: 14, fontWeight: 700, color: color.text, marginBottom: 10 };
const customWrap: React.CSSProperties = { marginTop: 10 };
const typeWrap: React.CSSProperties = { marginTop: 8 };
const toolsRow: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 };
const toolChip: React.CSSProperties = { fontSize: 12, color: color.textMuted, background: color.surfaceRaised, border: `1px solid ${color.border}`, borderRadius: 999, padding: '3px 10px' };

export default function SmartCreate({ open, initialPrompt, onClose, onInstalled }: SmartCreateProps) {
  const [step, setStep] = useState<Step>('describe');
  const [prompt, setPrompt] = useState(initialPrompt ?? '');
  const [kind, setKind] = useState<SmartCreateKind>('pipeline');
  const [questions, setQuestions] = useState<OrchestratorQuestion[]>([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [customText, setCustomText] = useState<Record<string, string>>({});
  const [clarifyNote, setClarifyNote] = useState<{ message: string; problems: string[] } | null>(null);
  const [draft, setDraft] = useState<OrchestratedManifestResult | null>(null);
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [schedTime, setSchedTime] = useState('');
  const [titleOverride, setTitleOverride] = useState('');
  const [busy, setBusy] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep('describe');
    setPrompt(initialPrompt ?? '');
    setKind('pipeline');
    setQuestions([]);
    setStepIndex(0);
    setAnswers({});
    setCustomText({});
    setClarifyNote(null);
    setDraft(null);
    setInputs({});
    setSchedTime('');
    setTitleOverride('');
    setBusy(false);
    setInstalling(false);
    setError(null);
  }, [open, initialPrompt]);

  const build = async (
    basePrompt: string,
    qs: OrchestratorQuestion[],
    ans: Record<string, string>,
    skipReview = false,
  ) => {
    setError(null);
    setBusy(true);
    setStep('building');
    try {
      const finalPrompt = composePrompt(basePrompt, qs, ans);
      const res = await marketplaceApi.orchestrateManifest({ prompt: finalPrompt, kind, skipReview });
      if (res.source !== 'llm') {
        setError('Не удалось собрать понятный план. Попробуйте переформулировать запрос.');
        setStep(qs.length ? 'clarify' : 'describe');
        return;
      }
      // Сервер увидел противоречия или нехватку данных — возвращаемся к уточнениям
      // и честно говорим, что именно неясно, вместо молчаливой сборки не того.
      if (res.status === 'questions' && Array.isArray(res.questions) && res.questions.length > 0) {
        setPrompt(finalPrompt);
        setQuestions(res.questions);
        setStepIndex(0);
        setAnswers({});
        setCustomText({});
        setClarifyNote({
          message: res.message || 'Уточните пару моментов — иначе сценарий может работать не так, как вы хотите',
          problems: Array.isArray(res.problems) ? res.problems : [],
        });
        setStep('clarify');
        haptic('light');
        return;
      }
      const nextInputs: Record<string, string> = {};
      for (const r of res.requirements ?? []) {
        if (r.default !== null && r.default !== undefined) nextInputs[r.ref] = String(r.default);
      }
      const trigger = res.manifest.trigger as Record<string, unknown> | undefined;
      setDraft(res);
      setInputs(nextInputs);
      setSchedTime(cronTime(trigger?.cron as string | undefined) ?? '');
      setTitleOverride(previewFromManifest(res.manifest).title);
      setClarifyNote(null);
      setStep('review');
      haptic('medium');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось собрать план');
      setStep(qs.length ? 'clarify' : 'describe');
    } finally {
      setBusy(false);
    }
  };

  const start = async () => {
    if (!prompt.trim()) { setError('Опишите, что нужно сделать'); return; }
    setError(null);
    setBusy(true);
    try {
      const res = await marketplaceApi.orchestrateClarify({ prompt: prompt.trim(), kind });
      if (res.status === 'questions' && res.questions.length > 0) {
        setQuestions(res.questions);
        setStepIndex(0);
        setAnswers({});
        setCustomText({});
        setClarifyNote(null);
        setStep('clarify');
      } else {
        await build(prompt.trim(), [], {});
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Помощник временно недоступен');
    } finally {
      setBusy(false);
    }
  };

  const current = questions[stepIndex];
  const pickAnswer = (value: string) => {
    if (!current) return;
    setAnswers((prev) => ({ ...prev, [current.id]: value }));
    haptic('light');
  };
  const canGoNext = (() => {
    if (!current) return false;
    const v = answers[current.id];
    if (!v) return false;
    if (v === CUSTOM_KEY) return String(customText[current.id] ?? '').trim().length > 0;
    return true;
  })();
  const goNext = () => {
    if (!canGoNext) return;
    if (stepIndex < questions.length - 1) { setStepIndex((i) => i + 1); return; }
    // Если это уже круг уточнений после ревизии — больше не переспрашиваем.
    void build(prompt, questions, resolveAnswers(questions, answers, customText), clarifyNote !== null);
  };
  const goBack = () => {
    if (stepIndex > 0) setStepIndex((i) => i - 1);
    else setStep('describe');
  };

  // Правки с экрана подтверждения (время, название) — прямо в манифест перед установкой.
  const editedManifest = (source: MarketplaceManifest): MarketplaceManifest => {
    const m = JSON.parse(JSON.stringify(source)) as any;
    const trigger = m.trigger as Record<string, unknown> | undefined;
    if (trigger && typeof trigger.cron === 'string' && schedTime.trim()) {
      trigger.cron = withCronTime(String(trigger.cron), schedTime);
    }
    const name = titleOverride.trim();
    if (name) {
      if (Array.isArray(m.agents) && m.agents.length > 0 && m.agents[0]) m.agents[0].name = name;
      if (m.agent && typeof m.agent === 'object') m.agent.name = name;
    }
    return m as MarketplaceManifest;
  };

  const install = async () => {
    if (!draft) return;
    setError(null);
    if (schedTime.trim() && !/^\d{1,2}:\d{2}$/.test(schedTime.trim())) {
      setError('Укажите время в формате ЧЧ:ММ');
      return;
    }
    for (const r of (draft.requirements ?? []) as RequirementView[]) {
      const v = String(inputs[r.ref] ?? '').trim();
      if (r.required && !v && (r.default === null || r.default === undefined)) {
        setError(`Заполните: ${r.label}`);
        return;
      }
    }
    const manifest = editedManifest(draft.manifest);
    const preview = previewFromManifest(manifest);
    setInstalling(true);
    try {
      await marketplaceApi.installManifest({ manifest, inputs, title: preview.title, summary: preview.description });
      haptic('medium');
      onInstalled?.({ kind: manifest.kind, title: preview.title, lines: preview.lines, tools: preview.tools });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка установки');
    } finally {
      setInstalling(false);
    }
  };

  const requirements = (draft?.requirements ?? []) as RequirementView[];
  // Превью считаем по УЖЕ отредактированному манифесту, чтобы строка «Когда»
  // менялась сразу при правке времени.
  const preview = draft ? previewFromManifest(editedManifest(draft.manifest)) : null;
  const canEditTime = Boolean(
    draft && typeof (draft.manifest.trigger as Record<string, unknown> | undefined)?.cron === 'string',
  );
  const choiceOptions: Choice[] = current
    ? [
        ...current.options.filter((o) => !isCustomOption(o)).map((o) => ({ key: o, label: o })),
        { key: CUSTOM_KEY, label: CUSTOM_LABEL },
      ]
    : [];
  const title =
    step === 'review' ? 'Вот что я сделаю'
    : step === 'clarify' ? 'Пара уточнений'
    : 'Что нужно сделать?';

  return (
    <Sheet open={open} onClose={onClose} title={title}>
      {step === 'describe' ? (
        <div>
          <p style={introText}>Опишите задачу словами — остальное я подберу сам.</p>
          <Field label="Что должно происходить">
            <TextArea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Например: каждое утро присылай дайджест новостей"
              rows={5}
            />
          </Field>
          <ExpertDisclosure label="Дополнительно">
            <div style={typeWrap}>
              <ChoiceChips stack options={KIND_OPTIONS} value={kind} onChange={(k) => setKind(k)} />
            </div>
          </ExpertDisclosure>
          <div style={footerWrap}>
            <Button fullWidth size="lg" onClick={start} loading={busy} disabled={!prompt.trim()}>
              Продолжить
            </Button>
          </div>
        </div>
      ) : null}

      {step === 'clarify' && current ? (
        <WizardStep
          step={stepIndex + 1}
          total={questions.length}
          question={current.text}
          onBack={goBack}
          footer={
            <Button fullWidth size="lg" onClick={goNext} loading={busy} disabled={!canGoNext}>
              {stepIndex < questions.length - 1 ? 'Далее' : 'Собрать план'}
            </Button>
          }
        >
          {clarifyNote ? (
            <div style={noteStyle}>
              <div style={{ fontWeight: 600 }}>{clarifyNote.message}</div>
              {clarifyNote.problems.map((p, i) => (
                <div key={i} style={noteItem}>• {p}</div>
              ))}
            </div>
          ) : null}
          <ChoiceChips stack options={choiceOptions} value={answers[current.id] ?? null} onChange={pickAnswer} />
          {answers[current.id] === CUSTOM_KEY ? (
            <div style={customWrap}>
              <Field label="Ваш вариант" hint="Напишите своими словами">
                <TextInput
                  value={customText[current.id] ?? ''}
                  placeholder="Например: USD/RUB и золото"
                  onChange={(e) => setCustomText((prev) => ({ ...prev, [current.id]: e.target.value }))}
                />
              </Field>
            </div>
          ) : null}
        </WizardStep>
      ) : null}

      {step === 'building' ? (
        <div style={loadingStyle}>Собираю понятный план…</div>
      ) : null}

      {step === 'review' && draft && preview ? (
        <div>
          <ResultCard icon={preview.emoji} title={preview.title} heading="Вот что я сделаю:" lines={preview.lines}>
            {preview.tools.length > 0 ? (
              <div style={toolsRow}>
                {preview.tools.map((t) => (
                  <span key={t} style={toolChip}>{t}</span>
                ))}
              </div>
            ) : null}
          </ResultCard>

          <div style={sectionWrap}>
            <div style={sectionTitle}>Можно поправить</div>
            <Field label="Название">
              <TextInput
                value={titleOverride}
                placeholder={preview.title}
                onChange={(e) => setTitleOverride(e.target.value)}
              />
            </Field>
            {canEditTime ? (
              <Field label="Время запуска" hint="Формат ЧЧ:ММ, по вашему часовому поясу">
                <TextInput
                  type="time"
                  value={schedTime}
                  placeholder="09:00"
                  onChange={(e) => setSchedTime(e.target.value)}
                />
              </Field>
            ) : null}
          </div>

          {requirements.length > 0 ? (
            <div style={sectionWrap}>
              <div style={sectionTitle}>Настройте под себя</div>
              {requirements.map((r) => (
                <Field key={r.ref} label={r.required ? `${r.label} *` : r.label} hint={r.description ?? undefined}>
                  <TextInput
                    type={fieldType(r.type)}
                    value={inputs[r.ref] ?? ''}
                    placeholder={r.default == null ? '' : String(r.default)}
                    onChange={(e) => setInputs((prev) => ({ ...prev, [r.ref]: e.target.value }))}
                  />
                </Field>
              ))}
            </div>
          ) : null}

          <div style={footerWrap}>
            <Button fullWidth size="lg" onClick={install} loading={installing}>
              Готово, создать
            </Button>
          </div>
        </div>
      ) : null}

      {error ? <div style={errorStyle}>{error}</div> : null}
    </Sheet>
  );
}
